import { DemoPaymentScenarioSchema } from '../sandbox/scenarios.js';
import { z } from 'zod';
import { BreBAliasTypeSchema, DisputeReasonSchema } from '../catalogs/domain-catalogs.js';
import { BusinessDateSchema, TimestampSchema } from '../common/clock.js';
import { AgentSessionIdSchema, CreditLineIdSchema, CustomerIdSchema, DisputeIdSchema, DocumentIdSchema, DrawdownIdSchema, IncomingRefundIdSchema, PayeeIdSchema, PaymentIntentIdSchema, ReconciliationExceptionIdSchema, ReconciliationRunIdSchema, ScaChallengeIdSchema, } from '../common/ids.js';
import { MoneySchema, PositiveMoneySchema } from '../common/money.js';
import { DateRangeQuerySchema, paginatedSchema, PaginationQuerySchema, } from '../common/pagination.js';
import { DrawdownSchema, PaymentAllocationSchema, SimulationResultSchema, StatementSchema, } from '../credit/schemas.js';
import { LenderProfileSchema } from '../identity/lender.js';
import { ReasonCodeSchema } from '../reason-codes.js';
import { FundingModeSchema } from './config.js';
import { DecisionChannelSchema, DisputeOutcomeSchema, DisputeStatusSchema, ExceptionSeveritySchema, IncomingRefundStatusSchema, PaymentIntentStatusSchema, PostingStatusSchema, ReconciliationExceptionStatusSchema, ReconciliationExceptionTypeSchema, ReconciliationRunStatusSchema, RefundMatchMethodSchema, SagaStepSchema, SagaStepStatusSchema, } from './states.js';
export const PaymentChannelSchema = z
    .enum(['APP', 'WIDGET', 'AGENT', 'API'])
    .meta({ id: 'PaymentChannel' });
// ── Beneficiarios y QR ────────────────────────────────────────────────────
export const PayeeAliasSchema = z
    .object({ aliasType: BreBAliasTypeSchema, aliasValue: z.string().min(1).max(100) })
    .meta({ id: 'PayeeAlias' });
export const PayeeSchema = z
    .object({
    id: PayeeIdSchema.optional(),
    name: z.string(),
    aliasType: BreBAliasTypeSchema,
    /** Enmascarado si es celular, documento o correo. */
    aliasValue: z.string(),
    participantCode: z.string(),
    participantName: z.string(),
    maskedAccount: z.string().optional(),
    mcc: z.string().optional(),
    city: z.string().optional(),
})
    .meta({ id: 'Payee' });
export const QrParseRequestSchema = z
    .object({ payload: z.string().min(10).max(1024) })
    .meta({ id: 'QrParseRequest' });
export const QrTypeSchema = z.enum(['STATIC', 'DYNAMIC']);
export const QrParseResponseSchema = z
    .object({
    qrType: QrTypeSchema,
    payee: z.object({
        aliasType: BreBAliasTypeSchema,
        aliasValue: z.string(),
        name: z.string(),
        participant: z.string(),
    }),
    amount: MoneySchema.optional(),
    reference: z.string().optional(),
    merchantCategory: z.string().optional(),
    city: z.string().optional(),
    crcValid: z.boolean(),
})
    .meta({ id: 'QrParseResponse' });
/**
 * `POST /v1/payees/resolve`: llave Bre-B tal como la escribe el cliente (`key`, sin tipo: el directorio
 * es único por valor) o, para integraciones que ya lo conocen, `aliasType` + `aliasValue`.
 */
export const ResolvePayeeRequestSchema = z
    .union([PayeeAliasSchema, z.object({ key: z.string().trim().min(1).max(100) })])
    .meta({ id: 'ResolvePayeeRequest' });
export const ResolvePayeeResponseSchema = z
    .object({ payee: PayeeSchema })
    .meta({ id: 'ResolvePayeeResponse' });
export const FrequentPayeeSchema = z
    .object({
    payee: PayeeSchema,
    nickname: z.string().nullable(),
    lastPaidAt: TimestampSchema.nullable(),
    count: z.int().min(0),
})
    .meta({ id: 'FrequentPayee' });
export const FrequentPayeeListSchema = z.object({ data: z.array(FrequentPayeeSchema) });
export const SetPayeeNicknameRequestSchema = z.object({
    nickname: z.string().trim().min(2).max(60),
});
// ── Intención de pago ─────────────────────────────────────────────────────
/**
 * `POST /v1/payment-intents` (Idem ✔). Exactamente uno de `payee` (llave), `qrPayload` o `payeeId`
 * (beneficiario del directorio, p. ej. un comercio frecuente cuya llave se muestra enmascarada).
 */
export const CreatePaymentIntentRequestSchema = z
    .object({
    lineId: CreditLineIdSchema,
    payee: PayeeAliasSchema.optional(),
    qrPayload: z.string().min(10).max(1024).optional(),
    payeeId: PayeeIdSchema.optional(),
    amount: PositiveMoneySchema,
    installments: z.int().min(1).max(120),
    description: z.string().max(140).optional(),
    channel: PaymentChannelSchema.exclude(['API']),
    agentSessionId: AgentSessionIdSchema.optional(),
    /** Solo con el modo demo: resultado de Bre-B que se quiere mostrar (menú del presentador). */
    demoScenario: DemoPaymentScenarioSchema.optional(),
})
    .refine((v) => [v.payee, v.qrPayload, v.payeeId].filter((x) => x !== undefined).length === 1, {
    message: 'Envía `payee`, `qrPayload` o `payeeId` (exactamente uno)',
    path: ['payee'],
})
    .meta({ id: 'CreatePaymentIntentRequest' });
export const PaymentStepSummarySchema = z.object({
    step: SagaStepSchema,
    status: SagaStepStatusSchema,
    attempt: z.int(),
    reasonCode: ReasonCodeSchema.nullable(),
    startedAt: TimestampSchema,
    finishedAt: TimestampSchema.nullable(),
});
export const ReceiptSchema = z
    .object({
    paymentIntentId: PaymentIntentIdSchema,
    receiptNumber: z.string(),
    payee: PayeeSchema,
    amount: MoneySchema,
    installments: z.int(),
    installmentAmount: MoneySchema,
    rateEA: z.string(),
    rateMV: z.string(),
    authCode: z.string(),
    endToEndId: z.string(),
    drawdownId: DrawdownIdSchema.nullable(),
    completedAt: TimestampSchema,
    receiptUrl: z.string(),
    description: z.string().nullable(),
    /** Quien paga y su entidad (la cuenta desde la que sale el giro Bre-B). */
    payer: z.object({
        name: z.string(),
        participantName: z.string(),
        maskedAccount: z.string().nullable(),
    }),
    /** Entidad que otorga el crédito (de `tenant.settings.lender`). */
    lender: z.object({ legalName: z.string(), nit: z.string().nullable() }),
})
    .meta({ id: 'Receipt' });
export const PaymentIntentSchema = z
    .object({
    id: PaymentIntentIdSchema,
    status: PaymentIntentStatusSchema,
    customerId: CustomerIdSchema,
    lineId: CreditLineIdSchema,
    payee: PayeeSchema,
    amount: MoneySchema,
    installments: z.int(),
    description: z.string().nullable(),
    channel: PaymentChannelSchema,
    agentSessionId: AgentSessionIdSchema.nullable(),
    fundingMode: FundingModeSchema,
    postingStatus: PostingStatusSchema,
    simulation: SimulationResultSchema,
    scaChallengeId: ScaChallengeIdSchema.nullable(),
    reasonCode: ReasonCodeSchema.nullable(),
    qrReference: z.string().nullable(),
    authCode: z.string().nullable(),
    endToEndId: z.string().nullable(),
    drawdownId: DrawdownIdSchema.nullable(),
    steps: z.array(PaymentStepSummarySchema).optional(),
    receipt: ReceiptSchema.nullable().optional(),
    createdAt: TimestampSchema,
    expiresAt: TimestampSchema,
    completedAt: TimestampSchema.nullable(),
})
    .meta({ id: 'PaymentIntent' });
/** Respuesta de `POST /v1/payment-intents`: en `DEMO_MODE` incluye el OTP del reto SCA (banner demo). */
export const CreatePaymentIntentResponseSchema = PaymentIntentSchema.extend({
    scaDemoCode: z.string().optional(),
}).meta({ id: 'CreatePaymentIntentResponse' });
export const ConfirmPaymentIntentResponseSchema = z
    .object({ status: z.literal('PROCESSING') })
    .meta({ id: 'ConfirmPaymentIntentResponse' });
export const ListPaymentIntentsQuerySchema = PaginationQuerySchema.extend(DateRangeQuerySchema.shape).extend({
    status: PaymentIntentStatusSchema.optional(),
});
export const PaymentIntentPageSchema = paginatedSchema(PaymentIntentSchema);
/** Progreso visible por SSE (`event: step`). */
export const PaymentProgressStepSchema = z.enum([
    'VALIDATED',
    'CREDIT_AUTHORIZED',
    'FUNDED',
    'PAYMENT_SENT',
    'PENDING_CONFIRMATION',
    'COMPLETED',
    'COMPENSATING',
    'FAILED',
]);
/** Eventos SSE de `GET /v1/payment-intents/{id}/events` (`retry: 2000`; se cierra en estado terminal). */
export const PaymentIntentSseEventSchema = z.discriminatedUnion('event', [
    z.object({
        event: z.literal('status'),
        data: z.object({ status: PaymentIntentStatusSchema, at: TimestampSchema }),
    }),
    z.object({
        event: z.literal('step'),
        data: z.object({ step: PaymentProgressStepSchema, at: TimestampSchema }),
    }),
    z.object({
        event: z.literal('completed'),
        data: z.object({
            status: z.literal('COMPLETED'),
            receiptUrl: z.string(),
            authCode: z.string(),
            endToEndId: z.string(),
        }),
    }),
    z.object({
        event: z.literal('failed'),
        data: z.object({
            status: z.literal('FAILED'),
            reasonCode: ReasonCodeSchema,
            message: z.string(),
            compensated: z.boolean(),
        }),
    }),
    z.object({
        event: z.literal('pending_confirmation'),
        data: z.object({
            status: z.literal('PENDING_CONFIRMATION'),
            since: TimestampSchema,
            nextCheckAt: TimestampSchema,
        }),
    }),
]);
export const SSE_RETRY_MS = 2000;
// ── Disputas y devoluciones ───────────────────────────────────────────────
export const CreateDisputeRequestSchema = z
    .object({
    paymentIntentId: PaymentIntentIdSchema,
    reason: DisputeReasonSchema,
    description: z.string().trim().min(5).max(1000),
    amount: PositiveMoneySchema.optional(),
    attachments: z.array(DocumentIdSchema).max(10).default([]),
})
    .meta({ id: 'CreateDisputeRequest' });
export const DisputeDecisionRequestSchema = z
    .object({
    outcome: DisputeOutcomeSchema,
    amount: PositiveMoneySchema.optional(),
    reasonCode: z.string().max(40),
    comment: z.string().max(2000),
})
    .refine((v) => v.outcome !== 'ACCEPTED_PARTIAL' || v.amount !== undefined, {
    message: '`amount` es obligatorio para ACCEPTED_PARTIAL',
    path: ['amount'],
})
    .meta({ id: 'DisputeDecisionRequest' });
export const DisputeSchema = z
    .object({
    id: DisputeIdSchema,
    paymentIntentId: PaymentIntentIdSchema,
    customerId: CustomerIdSchema,
    reason: DisputeReasonSchema,
    description: z.string(),
    amount: MoneySchema,
    status: DisputeStatusSchema,
    decision: DisputeOutcomeSchema.nullable(),
    decidedAmount: MoneySchema.nullable(),
    decisionChannel: DecisionChannelSchema.nullable(),
    reversal: z
        .object({ amount: MoneySchema, allocation: PaymentAllocationSchema.nullable() })
        .nullable(),
    dueAt: TimestampSchema,
    createdAt: TimestampSchema,
})
    .meta({ id: 'Dispute' });
export const DisputePageSchema = paginatedSchema(DisputeSchema);
export const IncomingRefundSchema = z
    .object({
    id: IncomingRefundIdSchema,
    endToEndId: z.string(),
    originalEndToEndId: z.string().nullable(),
    paymentIntentId: PaymentIntentIdSchema.nullable(),
    amount: MoneySchema,
    reference: z.string().nullable(),
    matchMethod: RefundMatchMethodSchema.nullable(),
    status: IncomingRefundStatusSchema,
    allocation: PaymentAllocationSchema.nullable(),
    receivedAt: TimestampSchema,
    appliedAt: TimestampSchema.nullable(),
})
    .meta({ id: 'IncomingRefund' });
export const IncomingRefundPageSchema = paginatedSchema(IncomingRefundSchema);
// ── Conciliación ──────────────────────────────────────────────────────────
export const ReconciliationExceptionSchema = z
    .object({
    id: ReconciliationExceptionIdSchema,
    runId: ReconciliationRunIdSchema.nullable(),
    type: ReconciliationExceptionTypeSchema,
    severity: ExceptionSeveritySchema,
    paymentIntentId: PaymentIntentIdSchema.nullable(),
    details: z.record(z.string(), z.unknown()),
    status: ReconciliationExceptionStatusSchema,
    resolutionNote: z.string().nullable(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'ReconciliationException' });
export const ReconciliationRunSchema = z
    .object({
    id: ReconciliationRunIdSchema,
    businessDate: BusinessDateSchema,
    status: ReconciliationRunStatusSchema,
    stats: z.record(z.string(), z.number()),
    startedAt: TimestampSchema,
    finishedAt: TimestampSchema.nullable(),
})
    .meta({ id: 'ReconciliationRun' });
/** Acciones de operador sobre transacciones: `reason` obligatorio (auditoría). */
export const OperatorActionRequestSchema = z.object({ reason: z.string().trim().min(5).max(1000) });
// ── Adjuntos de disputa ───────────────────────────────────────────────────
/** Reglas de carga de soportes de un reclamo (`POST /v1/disputes/attachments`, multipart `file`). */
export const DISPUTE_ATTACHMENT_RULES = {
    maxBytes: 10 * 1024 * 1024,
    maxFiles: 10,
    contentTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
};
/** Respuesta de la carga: el `id` (`doc_…`) va en `CreateDisputeRequest.attachments[]`. */
export const DisputeAttachmentSchema = z
    .object({
    id: DocumentIdSchema,
    fileName: z.string(),
    contentType: z.enum(DISPUTE_ATTACHMENT_RULES.contentTypes),
    sizeBytes: z.int().min(1).max(DISPUTE_ATTACHMENT_RULES.maxBytes),
    uploadedAt: TimestampSchema,
})
    .meta({ id: 'DisputeAttachment' });
// ── Vistas /v1 del crédito del cliente (proxy al Credit Core vía CreditCorePort) ───────
/** Utilización vista por el cliente: incluye la compra (`pi_…`) que la originó, si aplica. */
export const CustomerDrawdownSchema = DrawdownSchema.extend({
    paymentIntentId: PaymentIntentIdSchema.nullable(),
}).meta({ id: 'CustomerDrawdown' });
/** `GET /v1/credit/lines/{lineId}/drawdowns`. */
export const CustomerDrawdownListSchema = z
    .object({ data: z.array(CustomerDrawdownSchema), nextCursor: z.string().nullable() })
    .meta({ id: 'CustomerDrawdownList' });
/**
 * `GET /v1/credit/lines/{lineId}/statements/{statementId}`: extracto del core + identificación legal
 * de la entidad (NIT, Defensor del Consumidor Financiero) desde la configuración del tenant.
 */
export const CustomerStatementSchema = StatementSchema.extend({
    lender: LenderProfileSchema,
}).meta({ id: 'CustomerStatement' });
/** `GET /v1/credit/lines/{lineId}/statements`. */
export const StatementListSchema = z
    .object({ data: z.array(StatementSchema), nextCursor: z.string().nullable() })
    .meta({ id: 'StatementList' });
//# sourceMappingURL=schemas.js.map